import { logger } from './logger';

export type AnnouncePoliteness = 'polite' | 'assertive';

type Listener = (message: string, politeness: AnnouncePoliteness) => void;

/**
 * The app's one announcement channel.
 *
 * A module-level pub/sub rather than a context, so non-React callers (the toast policy in
 * `lib/toast`, the clipboard helper) can announce without a hook and without a second live region.
 * Two live regions interrupt each other and produce output no screen-reader user can follow — which
 * is the failure the single-region policy exists to prevent, and the reason `sonner` was removed
 * (ADR-015).
 */
const listeners = new Set<Listener>();

export function subscribeToAnnouncements(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `assertive` is reserved for the expiry warning and nothing else: it interrupts whatever the player
 * is currently having read to them, which is justified exactly once — when the money they are about
 * to send is going to arrive too late.
 */
export function announce(message: string, politeness: AnnouncePoliteness = 'polite'): void {
  if (message.trim() === '') return;

  if (listeners.size === 0) {
    // Not thrown: a missed announcement must never break a payment screen. Logged because a screen
    // that announces before `CashierLayout` mounts is a wiring bug we want to see in development.
    logger.warn('[a11y] announcement with no live region mounted', { politeness });
    return;
  }

  for (const listener of listeners) listener(message, politeness);
}
